import React, { FC, useMemo, useState } from 'react';
import {
    CreateChannelSubscriptionInput,
    GET_USER_PREMIUM_CHANNEL_SUBSCRIPTIONS,
} from '../../../gql/users';
import { Channel, ChannelSubscription } from '../../../shared/types';
import { useMutation, useQuery } from '@apollo/react-hooks';
import {
    Alert,
    Button,
    CircularProgress,
    Link,
    Snackbar,
    Stack,
    Typography,
} from '@mui/material';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import {
    getChannelSubscriptionInterestConfirmLink,
    getChannelSubscriptionLink,
} from '../../../utils/link.utils';
import {
    CREATE_OPEN_FREE_CHANNEL_SUBSCRIPTION,
    REQUEST_FREE_CLOSED_CHANNEL_SUBSCRIPTION,
} from '../../../gql/subscriptions';
import { useUser } from '../../providers/UserProvider';
import { useLogger } from '../../providers/logger/Logger';
import { LogAction, LogLevel } from '../../providers/logger/logger-types';
import { getDisplayProperties } from '../../../shared/channelSubscription';
import { formatDisplayDate } from '../../../utils/date.utils';
import { baseUrls } from '../../../utils/baseUrls.utils';
import { useRequestFreeTrial } from '../../hooks/use-request-free-trial';

export interface ChannelSubscriptionButtonProps {
    channel: Channel;
    disclaimer?: boolean;
    disclaimerText?: JSX.Element | string;
    size?: 'large' | 'medium' | 'small';
    align?: 'center' | 'left';
    onUpdate?: () => void;
}

export const ChannelSubscriptionButton: FC<ChannelSubscriptionButtonProps> = ({
    channel,
    disclaimer = true,
    size = 'medium',
    align = 'center',
    onUpdate = () => {},
}) => {
    const { userDetails } = useUser();
    const { printLog } = useLogger();
    const history = useHistory();
    const [freeOpenAlertVisible, setFreeOpenAlertVisible] = useState(false);
    const [freeClosedRequestAlertVisible, setFreeClosedRequestAlertVisible] =
        useState(false);
    const [trialRequestErrorVisible, setTrialRequestErrorVisible] =
        useState(false);
    // todo: a future enhancement would be to update the mutation so Appolo cache is automoatically updated
    const { data, loading, refetch } = useQuery(
        GET_USER_PREMIUM_CHANNEL_SUBSCRIPTIONS,
        { fetchPolicy: 'network-only' }
    );
    const [
        createFreeChannelSubscription,
        { loading: createFreeOpenSubscriptionLoading },
    ] = useMutation<
        ChannelSubscription,
        { item: CreateChannelSubscriptionInput }
    >(CREATE_OPEN_FREE_CHANNEL_SUBSCRIPTION);
    const [
        requestFreeClosedChannelSubscription,
        { loading: requestFreeClosedChannelSubscriptionLoading },
    ] = useMutation<ChannelSubscription, { channelId: string }>(
        REQUEST_FREE_CLOSED_CHANNEL_SUBSCRIPTION
    );
    const [requestTrial, loadingRequestTrial] = useRequestFreeTrial();

    const channelSub = useMemo(
        () =>
            data?.premiumChannelSubscriptions?.find(
                (pc: ChannelSubscription) => pc.channelId === channel._id
            ),
        [channel._id, data]
    );

    const {
        freeClosedRequestRejected,
        freeClosedRequestPending,
        isFreeOpenChannel,
        isFreeClosedChannel,
        isPremiumChannel,
        isSubscriptionActive,
        isTrialSubscriptionPending,
        isTrialSubscriptionActive,
    } = useMemo(
        () => getDisplayProperties(channelSub, channel),
        [channelSub, channel]
    );

    const subscriptionHandler = () => {
        printLog(
            LogLevel.INFO,
            LogAction.RegisteredInterestPremiumChannel,
            LogAction.RegisteredInterestPremiumChannel,
            {
                channelId: channel._id,
                channelName: channel.name,
                userEmail: userDetails?.email,
            }
        );

        window.Appcues?.track('PremiumChannelSubscribeClick', {
            channelName: channel.name,
            userEmail: userDetails?.email,
        });

        if (isPremiumChannel) {
            requestTrial(channel._id)
                .then(() =>
                    history.push(
                        getChannelSubscriptionInterestConfirmLink(channel._id) +
                            '?trial'
                    )
                )
                .catch(() => setTrialRequestErrorVisible(true));
        }

        if (isFreeOpenChannel) {
            createFreeChannelSubscription({
                variables: {
                    item: {
                        channelId: channel._id,
                    },
                },
            }).then(() => {
                refetch();
                onUpdate();
                setFreeOpenAlertVisible(true);
            });
        }

        if (isFreeClosedChannel) {
            requestFreeClosedChannelSubscription({
                variables: {
                    channelId: channel._id,
                },
            }).then(() => {
                refetch();
                onUpdate();
                setFreeClosedRequestAlertVisible(true);
            });
        }
    };

    const buttonText = () => {
        if (isTrialSubscriptionPending) return 'Trial request pending';

        if (isFreeOpenChannel) return 'Subscribe for free';

        if (freeClosedRequestPending) return 'Access request pending';

        if (isFreeClosedChannel) return 'Request access for free';

        return 'Request free trial';
    };

    if (loading) {
        return <></>;
    }

    const disclaimerContent =
        isSubscriptionActive && !isPremiumChannel ? (
            ''
        ) : isFreeOpenChannel ? (
            <>
                Get email notifications when new <br />
                articles are published
            </>
        ) : isFreeClosedChannel ? (
            freeClosedRequestPending ? (
                <>
                    Your request is currently under review,
                    <br />
                    check your inbox for updates
                </>
            ) : (
                <>
                    Become a channel subscriber to get free
                    <br />
                    and unlimited access to all articles
                </>
            )
        ) : isTrialSubscriptionActive ? (
            `Your free trial access ends on ${formatDisplayDate(channelSub.cancelAt, false)}`
        ) : isTrialSubscriptionPending ? (
            <>
                Don't want to wait?{' '}
                <Link
                    component={RouterLink}
                    to={getChannelSubscriptionLink(channel._id)}
                >
                    Buy now
                </Link>
            </>
        ) : (
            <>
                This is a premium subscription model.
                <br />
                Once started, plan automatically renews until cancelled.
            </>
        );

    const showPricingOptions =
        isPremiumChannel &&
        (!isSubscriptionActive || isTrialSubscriptionActive);
    const actionLoading =
        createFreeOpenSubscriptionLoading ||
        requestFreeClosedChannelSubscriptionLoading ||
        loadingRequestTrial;

    return (
        <>
            <Snackbar
                open={freeOpenAlertVisible}
                onClose={() => setFreeOpenAlertVisible(false)}
                autoHideDuration={5000}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    severity="info"
                    onClose={() => setFreeOpenAlertVisible(false)}
                >
                    Thanks for becoming a subscriber! You will receive email
                    notifications when new articles are published.
                </Alert>
            </Snackbar>
            <Snackbar
                open={freeClosedRequestAlertVisible}
                onClose={() => setFreeClosedRequestAlertVisible(false)}
                autoHideDuration={5000}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    severity="info"
                    onClose={() => setFreeClosedRequestAlertVisible(false)}
                >
                    Thanks for requesting access to the channel. The Channel
                    Owners will review your request. Watch your inbox for
                    further communications!
                </Alert>
            </Snackbar>
            <Snackbar
                open={trialRequestErrorVisible}
                onClose={() => setTrialRequestErrorVisible(false)}
                autoHideDuration={5000}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    severity="error"
                    onClose={() => setTrialRequestErrorVisible(false)}
                >
                    We could not request your free trial. Please try again.
                </Alert>
            </Snackbar>

            <Stack
                spacing={1}
                sx={{
                    alignItems: align === 'center' ? 'center' : 'flex-start',
                }}
            >
                <Stack direction="row" spacing={1}>
                    {((isPremiumChannel &&
                        (!channelSub || isTrialSubscriptionPending)) ||
                        (!isPremiumChannel && !isSubscriptionActive)) && (
                        <Button
                            id="subscribe-cta"
                            variant="contained"
                            color="primary"
                            size={size}
                            onClick={subscriptionHandler}
                            disabled={
                                isTrialSubscriptionPending ||
                                freeClosedRequestRejected ||
                                freeClosedRequestPending ||
                                actionLoading
                            }
                            startIcon={
                                actionLoading ? (
                                    <CircularProgress
                                        color="inherit"
                                        size={16}
                                    />
                                ) : undefined
                            }
                        >
                            {buttonText()}
                        </Button>
                    )}
                    {showPricingOptions && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            size={size}
                            onClick={() =>
                                history.push(
                                    getChannelSubscriptionLink(channel._id)
                                )
                            }
                        >
                            See pricing options
                        </Button>
                    )}
                    {isSubscriptionActive && !showPricingOptions && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            size={size}
                            onClick={() => history.push(baseUrls.subscriptions)}
                        >
                            Manage subscriptions
                        </Button>
                    )}
                </Stack>
                {disclaimer && (
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'text.secondary',
                            textAlign: align === 'center' ? 'center' : 'left',
                        }}
                    >
                        {disclaimerContent}
                    </Typography>
                )}
            </Stack>
        </>
    );
};
